import { $, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The editor of one sticky message.
 *
 * Two numbers and a text. Saving posts it at the bottom of its channel straight
 * away — the page says so, and the server does it, because a sticky that waits
 * for somebody to talk is indistinguishable from one that is broken.
 */
interface Sticky {
  _id: string;
  channelId: string;
  content: string;
  templateName: string;
  afterMessages: number;
  afterSeconds: number;
  enabled: boolean;
}

export function initStickyEditorPage(): void {
  const form = $<HTMLFormElement>('[data-sticky-form]');
  if (!form) return;

  const { sticky, guildId } = pageData<{ sticky: Sticky; guildId: string }>();

  fillForm(form, { ...sticky });

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = sticky.channelId ? [sticky.channelId] : [];

  // Ticked by default on a creation: somebody who has just written a sticky
  // meant it to stick. `fillForm` cannot guess it — the document does not exist.
  const picker = form.elements.namedItem('templateName');
  if (picker instanceof HTMLSelectElement) picker.value = sticky.templateName;

  const enabled = form.elements.namedItem('enabled');
  if (enabled instanceof HTMLInputElement) enabled.checked = sticky._id ? sticky.enabled : true;

  mountResourceEditor({
    endpoint: 'sticky',
    listUrl: `/modules/sticky?guildId=${guildId}`,
    guildId,
    form,
    itemId: sticky._id,

    serialize: () => ({
      channelId: findSelectMenu(form, 'channelId')?.single ?? '',
      content: text(form, 'content'),
      templateName: select(form, 'templateName'),
      afterMessages: number(form, 'afterMessages', 5),
      afterSeconds: number(form, 'afterSeconds', 60),
      enabled: checked(form, 'enabled'),
      publish: false,
    }),

    labels: {
      saved: ct('stickySaved', 'Sticky saved and posted.'),
      confirmDelete: ct('confirmDeleteSticky', 'Delete this sticky message?'),
      deleted: ct('stickyDeleted', 'Sticky deleted.'),
    },
  });
}

/** What a plain `<select>` holds — the template picker is not a select-menu widget. */
function select(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLSelectElement ? field.value : '';
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement
    ? field.value
    : '';
}

function number(form: HTMLFormElement, name: string, fallback: number): number {
  const field = form.elements.namedItem(name);
  const value = field instanceof HTMLInputElement ? Number(field.value) : NaN;
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function checked(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
